import { HitTestResult } from '@chartv2/engine/hit-test';
import { clsx } from 'clsx';
import React, { cloneElement } from 'react';

import type {
  ChartV2LegendMoreItem,
  ChartV2ResolvedSeries,
} from '../types/chart-v2.types';
import { CloseIcon, EyeIcon, EyeOffIcon } from './Icons';
import styles from './Legend.module.scss';
import { LegendMoreMenu } from './LegendMoreMenu';

export interface LegendItemEntry {
  series: ChartV2ResolvedSeries;
  hidden?: boolean;
  moreMenuItems?: ChartV2LegendMoreItem[];
}

export interface LegendItemLocales {
  more?: string;
  hide?: string;
  show?: string;
  remove?: string;
}

export interface LegendItemProps {
  entry: LegendItemEntry;
  onToggle?: (seriesId: string, isCurrentlyHidden: boolean) => void;
  onRemove?: (seriesId: string) => void;
  locales?: LegendItemLocales;
  hoverHit?: HitTestResult;
}

/**
 * One row in the legend — label slot on the left, the consumer's
 * status node on the right (resting state), and a hover-only icon
 * group with the more-menu / hide / remove actions.
 */
export const LegendItem = ({
  entry,
  onToggle,
  onRemove,
  locales,
  hoverHit,
}: LegendItemProps) => {
  const { series, hidden, moreMenuItems } = entry;
  const hasMoreMenu = (moreMenuItems?.length ?? 0) > 0;
  const toggleTitle = hidden
    ? (locales?.show ?? 'Show series')
    : (locales?.hide ?? 'Hide series');
  const removeTitle = locales?.remove ?? 'Remove series';

  return (
    <li
      className={clsx(styles.item, hidden && styles.hidden)}
      data-testid={`chartv2-legend-item-${series.id}`}
    >
      <div
        className={styles.itemHeader}
        data-testid={`chartv2-legend-label-${series.id}`}
      >
        {series.legendLabel ?? (
          <span className={styles.fallbackLabel}>{series.label}</span>
        )}
      </div>

      <div className={styles.itemBody}>
        {!!series.legendStatus && (
          <div
            className={styles.restingContent}
            data-testid={`chartv2-legend-status-${series.id}`}
          >
            {/* use SeriesLegendStatus component instead of cloneElement once chartv2 is merged */}
            {cloneElement(series.legendStatus, { hoverHit })}
          </div>
        )}

        <div className={styles.hoverActions}>
          {hasMoreMenu && moreMenuItems && (
            <LegendMoreMenu
              items={moreMenuItems}
              seriesId={series.id}
              triggerTitle={locales?.more}
            />
          )}
          {onToggle && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => onToggle(series.id, !!hidden)}
              aria-label={toggleTitle}
              title={toggleTitle}
              data-testid={`chartv2-legend-toggle-${series.id}`}
            >
              {hidden ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => onRemove(series.id)}
              aria-label={removeTitle}
              title={removeTitle}
              data-testid={`chartv2-legend-remove-${series.id}`}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>
    </li>
  );
};
